import { KIND_BUCKETS } from './adventure.js';
import { resolveKind, BUILTIN_KINDS, normalizeKindId } from './kinds.js';
import { parseRef, extractWikiLinks, formatRef } from './refs.js';
/** Resolve the field key that carries an entity's display label (role:title), if any. */
function labelKey(kind, customKinds) {
    const def = resolveKind(kind, customKinds);
    return def?.fields.find((f) => f.role === 'title')?.key;
}
function entityLabel(entity, key) {
    const candidates = [key, 'name', 'title', 'id'].filter(Boolean);
    for (const k of candidates) {
        const v = entity[k];
        if (typeof v === 'string' && v.trim())
            return v.trim();
    }
    return String(entity.id ?? '');
}
/** Every entity in the adventure, across built-in buckets, pages, and custom kinds. */
export function listEntities(adv) {
    const out = [];
    const customKinds = (adv.kinds ?? {});
    const push = (kind, list) => {
        if (!Array.isArray(list))
            return;
        const key = labelKey(kind, customKinds);
        const icon = BUILTIN_KINDS[kind]?.icon;
        for (const entity of list) {
            const id = String(entity.id ?? '');
            if (!id)
                continue;
            out.push({
                kind,
                id,
                ref: formatRef(kind, id),
                entity,
                label: entityLabel(entity, key),
                icon: (typeof entity.icon === 'string' && entity.icon) || icon,
            });
        }
    };
    for (const { kind, bucket } of KIND_BUCKETS) {
        const list = bucket === 'rooms'
            ? adv.rooms.rooms
            : adv[bucket];
        push(kind, list);
    }
    push('page', adv.pages);
    for (const [kind, list] of Object.entries(adv.entities ?? {})) {
        push(normalizeKindId(kind), list);
    }
    return out;
}
export function buildEntityIndex(adv) {
    const byRef = new Map();
    const byName = new Map();
    for (const e of listEntities(adv)) {
        byRef.set(e.ref, e);
        const name = typeof e.entity.name === 'string' ? e.entity.name : undefined;
        if (name)
            byName.set(`${e.kind}:${name.toLowerCase()}`, e);
    }
    return { byRef, byName };
}
/**
 * Resolve a reference to an indexed entity. Accepts:
 *  - a namespaced `"kind:id"` string,
 *  - a bare id when `fromKind` is supplied (e.g. an encounter's `monsters[].id` → beast),
 *  - a name (with `fromKind`), matched case-insensitively.
 */
export function resolveRef(index, ref, fromKind) {
    if (!ref)
        return undefined;
    const raw = typeof ref === 'string' ? ref : (ref.ref ?? ref.id ?? '');
    if (!raw)
        return undefined;
    const parsed = parseRef(raw);
    if (parsed)
        return index.byRef.get(formatRef(parsed.kind, parsed.id));
    if (fromKind) {
        const kind = normalizeKindId(fromKind);
        return index.byRef.get(formatRef(kind, raw)) ?? index.byName.get(`${kind}:${raw.toLowerCase()}`);
    }
    return undefined;
}
/**
 * Map of `"kind:id"` → entities that reference it. Only explicit `kind:id` references and
 * `[[kind:id]]` wiki-links are indexed (bare-id/name relations stay loose until namespaced).
 */
export function buildBacklinkIndex(adv) {
    const out = new Map();
    const add = (targetRef, from, field) => {
        const list = out.get(targetRef) ?? [];
        if (!list.some((b) => b.from.ref === from.ref && b.field === field))
            list.push({ from, field });
        out.set(targetRef, list);
    };
    for (const from of listEntities(adv)) {
        const visit = (val, field) => {
            if (typeof val === 'string') {
                const r = parseRef(val);
                if (r)
                    add(formatRef(r.kind, r.id), from, field);
                for (const w of extractWikiLinks(val))
                    add(formatRef(w.kind, w.id), from, field);
            }
            else if (Array.isArray(val)) {
                for (const item of val)
                    visit(item, field);
            }
            else if (val && typeof val === 'object') {
                const obj = val;
                if (typeof obj.ref === 'string')
                    visit(obj.ref, field);
            }
        };
        for (const [field, val] of Object.entries(from.entity)) {
            if (field === 'id')
                continue;
            visit(val, field);
        }
    }
    return out;
}
//# sourceMappingURL=linking.js.map